import {
  addCustomer,
  useGetPlatformSummary,
  useListPlatformOrgs,
  useStartSupportSession,
  type PlatformOrgOut,
} from '@myrentalmate/api-client';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';

import { DataTable, type DataColumn } from '../../components/DataTable';
import { FormDialog } from '../../components/FormDialog';
import { EMAIL_FIELD, required, type Field } from '../../components/FormFields';
import { InviteLinkDialog } from '../../components/InviteLinkDialog';
import { Placeholder } from '../../components/Placeholder';
import { StatRow, StatTile } from '../../components/Stats';
import { okData, okItems } from '../../lib/apiData';
import { apiErrorMessage } from '../../lib/apiError';
import { formatDateTime } from '../../lib/format';
import { TIMEZONES } from '../../lib/timezones';
import { TablePager, useTablePage } from '../../components/TablePager';

function OpenCustomerDialog({ org, onClose }: { org: PlatformOrgOut; onClose: () => void }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [reason, setReason] = useState('');
  const start = useStartSupportSession({
    mutation: {
      onSuccess: () => {
        // Nothing from the previous view may linger on screen.
        queryClient.clear();
        navigate('/staff');
      },
    },
  });
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <Box
        component="form"
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          start.mutate({ data: { org_id: org.id, reason } });
        }}
      >
        <DialogTitle>Open {org.name}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography color="text.secondary">
              You'll be able to view and change this customer's records for one hour. Everything you
              change is recorded in their audit log with this reason.
            </Typography>
            {start.isError && <Alert severity="error">{apiErrorMessage(start.error)}</Alert>}
            <TextField
              label="Reason"
              required
              autoFocus
              multiline
              minRows={2}
              helperText="The help desk ticket, or why you need access"
              value={reason}
              slotProps={{ htmlInput: { minLength: 5, maxLength: 500 } }}
              onChange={(e) => setReason(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={start.isPending}>
            Open customer
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

const NEW_CUSTOMER_FIELDS: Field[] = [
  { name: 'name', label: 'Business Name', required: true },
  {
    ...EMAIL_FIELD,
    name: 'admin_email',
    label: "Their Admin's Email",
    required: true,
    helperText: "We'll email them an invitation to set up their account",
  },
  {
    name: 'timezone',
    label: 'Timezone',
    type: 'select',
    required: true,
    options: TIMEZONES,
  },
];

function NewCustomerDialog({
  onClose,
  onAdded,
}: {
  onClose: () => void;
  onAdded: (url: string) => void;
}) {
  // FormDialog closes itself after saving, so the link is handed over at that moment.
  const link = useRef<string | null>(null);
  return (
    <FormDialog
      title="Add Customer"
      fields={NEW_CUSTOMER_FIELDS}
      initial={{ name: '', admin_email: '', timezone: 'America/New_York' }}
      submitLabel="Add customer"
      onSubmit={async (values) => {
        const created = okData(
          await addCustomer({
            name: required(values, 'name'),
            admin_email: required(values, 'admin_email'),
            timezone: required(values, 'timezone'),
          }),
        );
        link.current = created?.invite_url ?? null;
      }}
      onClose={() => (link.current ? onAdded(link.current) : onClose())}
    />
  );
}

/** The platform owner's area. Its API routes each check is_platform_admin on the server. */
export function PlatformHome() {
  const summary = okData(useGetPlatformSummary().data);
  const [opening, setOpening] = useState<PlatformOrgOut | null>(null);
  const [adding, setAdding] = useState(false);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const paging = useTablePage();
  const query = useListPlatformOrgs({ q, ...paging.params });
  const orgs = okItems(query.data);
  const total = okData(query.data)?.total ?? 0;
  const columns: DataColumn<PlatformOrgOut>[] = [
    { label: 'Business', render: (org) => org.name },
    { label: 'Sign-in address', render: (org) => `/o/${org.slug}` },
    { label: 'Joined', render: (org) => formatDateTime(org.created_at) },
    {
      label: 'Actions',
      align: 'right',
      render: (org) => (
        <Button size="small" onClick={() => setOpening(org)}>
          Open
        </Button>
      ),
    },
  ];
  return (
    <Stack spacing={3}>
      <Typography variant="h4">Platform Admin</Typography>
      <StatRow>
        <StatTile label="Customers" value={summary?.orgs} />
        <StatTile label="Users" value={summary?.users} />
        <StatTile label="Signed In, Last 7 Days" value={summary?.users_signed_in_last_7_days} />
      </StatRow>
      <Stack spacing={1}>
        <Stack direction="row" useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Customers
          </Typography>
          <TextField
            size="small"
            placeholder="Search"
            slotProps={{ htmlInput: { 'aria-label': 'Search customers' } }}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              paging.setPage(0);
            }}
          />
          <Button variant="contained" onClick={() => setAdding(true)}>
            Add customer
          </Button>
        </Stack>
        {orgs.length === 0 && !query.isPending ? (
          <Typography color="text.secondary">
            {q ? `Nothing matches "${q}".` : 'No customers yet.'}
          </Typography>
        ) : (
          <DataTable columns={columns} rows={orgs} rowKey={(org) => org.id} />
        )}
        <TablePager paging={paging} total={total} />
      </Stack>
      {opening && <OpenCustomerDialog org={opening} onClose={() => setOpening(null)} />}
      {adding && (
        <NewCustomerDialog
          onClose={() => setAdding(false)}
          onAdded={(url) => {
            setAdding(false);
            setInviteUrl(url);
          }}
        />
      )}
      {inviteUrl && <InviteLinkDialog url={inviteUrl} onClose={() => setInviteUrl(null)} />}
      <Placeholder title="App billing" stage="Stage 9" />
      <Placeholder title="Help desk tickets" stage="a later stage" />
    </Stack>
  );
}
